import { useEffect, useMemo, useState, type RefObject } from 'react'
import type { Block, Narration } from '../../lib/types'
import { activeBlockAt, buildTimeline, narrationTiming, startOf } from '../../lib/narration'
import { PLAYBACK_RATES } from '../../lib/useAudioPlayer'
import { useNarrationPlayer } from '../../lib/useNarrationPlayer'
import { formatDuration } from '../../lib/audio'
import { PauseGlyph, PlayGlyph } from '../audio/icons'
import { Scrubber } from '../audio/Waveform'

/** What the article can ask of the player ("Read from here"). */
export interface ListenController {
  playFrom(blockId: string): void
}

export interface ListenState {
  /** The block being read, once playback has started. */
  activeId: string | null
  playing: boolean
  /** Playback has started (playing, or paused part-way). */
  started: boolean
  /** The reader asked the page to scroll along with the narration (off by default). */
  follow: boolean
}

interface Props {
  narration: Narration
  blocks: Block[]
  authorName: string
  onState(state: ListenState): void
  controllerRef: RefObject<ListenController | null>
  className?: string
}

const rateLabel = (r: number) => `${r}×`

/**
 * "Listen · Niki reads this writing" (design 4b). Owns the audio clock so its
 * frame-by-frame updates re-render only this card; the article hears about
 * the block being read only when it changes.
 */
export function ListenPlayer({ narration, blocks, authorName, onState, controllerRef, className = '' }: Props) {
  // A single file, or pieces recorded paragraph by paragraph and played back
  // to back; either way one timeline, and pieces mark where their blocks start.
  const player = useNarrationPlayer(narration)
  const duration = player.duration || narration.duration
  const marks = useMemo(() => narrationTiming(narration).marks, [narration])
  const timeline = useMemo(() => buildTimeline(blocks, { duration, marks }), [blocks, duration, marks])
  const started = player.playing || player.time > 0
  const activeId = started ? activeBlockAt(timeline, player.time) : null
  // The reader leads: the page only scrolls along with the narration if they ask.
  const [follow, setFollow] = useState(false)

  useEffect(() => {
    onState({ activeId, playing: player.playing, started, follow })
  }, [onState, activeId, player.playing, started, follow])

  // Fresh closures every render; the article calls this from a click.
  useEffect(() => {
    controllerRef.current = {
      playFrom(blockId) {
        const at = startOf(timeline, blockId)
        if (at === null) return
        player.seek(at)
        if (!player.playing) player.play()
      },
    }
  })
  useEffect(() => () => void (controllerRef.current = null), [controllerRef])

  const nextRate = () => player.setRate(PLAYBACK_RATES[(PLAYBACK_RATES.indexOf(player.rate) + 1) % PLAYBACK_RATES.length] ?? 1)

  return (
    <div role="group" aria-label="Listen" className={`flex items-center gap-3.5 rounded-2xl border border-card-edge bg-card px-4 py-3 font-sans ${className}`}>
      <button
        type="button"
        onClick={player.toggle}
        aria-label={player.playing ? 'Pause narration' : 'Play narration'}
        className="flex size-[34px] shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground transition-opacity hover:opacity-90"
      >
        {player.playing ? <PauseGlyph /> : <PlayGlyph />}
      </button>
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <div className="flex items-baseline justify-between gap-3 text-[13px] font-medium">
          <span className="truncate text-foreground">
            <span className="hidden sm:inline">Listen · </span>
            {authorName} reads this writing
          </span>
          <span className="shrink-0 tabular-nums text-muted-foreground">
            {formatDuration(player.time)} / {formatDuration(duration)}
          </span>
        </div>
        <Scrubber time={player.time} duration={duration} onSeek={player.seek} label="Seek narration" className="-my-1" />
        {player.error && (
          <p role="alert" className="m-0 text-[11.5px] font-medium text-primary">
            {player.error}
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={() => setFollow((f) => !f)}
        aria-pressed={follow}
        title={follow ? 'The page scrolls along with the narration' : 'Scroll the page along with the narration'}
        className={`shrink-0 rounded-full px-2 py-1 text-[13px] font-medium transition-colors ${
          follow ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-foreground/5 hover:text-foreground'
        }`}
      >
        Follow
      </button>
      <button
        type="button"
        onClick={nextRate}
        aria-label={`Playback speed ${rateLabel(player.rate)}`}
        className="shrink-0 rounded-full px-1.5 py-1 text-[12px] font-medium tabular-nums text-muted-foreground hover:bg-foreground/5 hover:text-foreground"
      >
        {rateLabel(player.rate)}
      </button>
    </div>
  )
}
